import React, { useRef, useState } from 'react';
import { motion, useInView } from 'framer-motion';
import { Input } from './ui/input';
import { useLanguage } from '../i18n/LanguageContext';

const FORMSPREE = 'https://formspree.io/f/mreyjwnd';

function usePilotForm() {
  const [fields, setFields] = useState({ name: '', email: '', org: '' });
  const [status, setStatus] = useState('idle'); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = useState('');

  const set = (key) => (e) => setFields(f => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(FORMSPREE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ _subject: 'Pilot Interest — Canopi', ...fields }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.error || `Error ${res.status}`);
        setStatus('error');
      }
    } catch (err) {
      setErrorMsg(err.message || 'Network error');
      setStatus('error');
    }
  };

  return { fields, set, status, errorMsg, submit };
}

function useCommunityForm() {
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMsg('');
    try {
      const res = await fetch(FORMSPREE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ _subject: 'Community Waitlist — Canopi', email }),
      });
      if (res.ok) {
        setStatus('success');
      } else {
        const data = await res.json().catch(() => ({}));
        setErrorMsg(data.error || `Error ${res.status}`);
        setStatus('error');
      }
    } catch (err) {
      setErrorMsg(err.message || 'Network error');
      setStatus('error');
    }
  };

  return { email, setEmail, status, errorMsg, submit };
}

export default function ExpressInterestSection() {
  const { t } = useLanguage();
  const sectionRef = useRef(null);
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' });

  const pilot     = usePilotForm();
  const community = useCommunityForm();

  return (
    <section id="interest" className="relative py-24 sm:py-32 px-6 sm:px-12 lg:px-24" data-testid="express-interest-section">
      <div className="max-w-4xl mx-auto">

        {/* ── Closing headline ── */}
        <motion.div
          ref={sectionRef}
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-14"
        >
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-extralight tracking-tight leading-tight max-w-2xl mx-auto">
            {t('interest.closingHeading')}{' '}
            <span className="text-[#BDD6B5]">{t('interest.closingHeadingGreen')}</span>
          </h2>
          <p className="mt-6 text-base sm:text-lg text-white/72 max-w-xl mx-auto leading-relaxed">
            {t('interest.closingBody')}
          </p>

          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <a href="#pilot-form" className="bg-[#3D5638] hover:bg-[#4A6845] text-white rounded-full px-9 py-4 font-medium tracking-wide transition-all duration-300 shadow-[0_0_24px_rgba(61,86,56,0.5)] hover:shadow-[0_0_40px_rgba(61,86,56,0.65)]">
              {t('interest.ctaPilot')}
            </a>
            <a href="#community-form" className="bg-black/20 backdrop-blur-sm border border-white/25 hover:border-white/50 text-white rounded-full px-9 py-4 font-medium transition-all duration-300">
              {t('interest.ctaWaitlist')}
            </a>
          </div>
        </motion.div>

        {/* Divider */}
        <div className="relative h-px mx-0 mb-14 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-[#3D5638]/40 to-transparent" />
        </div>

        {/* ── Pilot Interest Form ── */}
        <motion.div
          id="pilot-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
        >
          <div className="glass-card p-8 sm:p-12 mb-8">
            <h3 className="text-2xl sm:text-3xl tracking-tight font-medium mb-4">
              {t('interest.pilotHeading')}{' '}
              <span className="text-[#BDD6B5]">{t('interest.pilotHeadingGreen')}</span>
            </h3>
            <p className="text-base text-white/78 mb-8 max-w-xl leading-relaxed">
              {t('interest.pilotBody')}
            </p>

            {pilot.status === 'success' ? (
              <div className="max-w-md py-6">
                <p className="text-[#BDD6B5] font-medium mb-1">Request received — thank you.</p>
                <p className="text-sm text-white/50">We'll be in touch when PNW pilots open.</p>
              </div>
            ) : (
              <form onSubmit={pilot.submit} className="space-y-4 max-w-md">
                <Input
                  type="text"
                  required
                  placeholder={t('interest.namePlaceholder')}
                  value={pilot.fields.name}
                  onChange={pilot.set('name')}
                  data-testid="interest-name-input"
                  className="bg-[#0E110E]/60 border-[#3D5638]/30 focus:border-[#BDD6B5] text-white placeholder:text-white/30 rounded-xl h-12"
                />
                <Input
                  type="email"
                  required
                  placeholder={t('interest.emailPlaceholder')}
                  value={pilot.fields.email}
                  onChange={pilot.set('email')}
                  data-testid="interest-email-input"
                  className="bg-[#0E110E]/60 border-[#3D5638]/30 focus:border-[#BDD6B5] text-white placeholder:text-white/30 rounded-xl h-12"
                />
                <Input
                  type="text"
                  placeholder={t('interest.orgPlaceholder')}
                  value={pilot.fields.org}
                  onChange={pilot.set('org')}
                  data-testid="interest-org-input"
                  className="bg-[#0E110E]/60 border-[#3D5638]/30 focus:border-[#BDD6B5] text-white placeholder:text-white/30 rounded-xl h-12"
                />
                {pilot.status === 'error' && (
                  <p className="text-red-400/80 text-xs">Something went wrong — {pilot.errorMsg || 'please try again'}.</p>
                )}
                <button
                  type="submit"
                  disabled={pilot.status === 'submitting'}
                  data-testid="interest-submit-button"
                  className="bg-[#3D5638] hover:bg-[#4A6845] disabled:opacity-50 text-white rounded-full px-8 py-3 font-medium tracking-wide transition-all duration-300 shadow-[0_0_20px_rgba(61,86,56,0.3)] hover:shadow-[0_0_30px_rgba(61,86,56,0.5)] w-full sm:w-auto"
                >
                  {pilot.status === 'submitting' ? 'Sending…' : t('interest.submitPilot')}
                </button>
                <p className="text-xs text-white/30 mt-2">{t('interest.disclaimer')}</p>
              </form>
            )}
          </div>
        </motion.div>

        {/* ── Community Waitlist Form ── */}
        <motion.div
          id="community-form"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7, delay: 0.1 }}
        >
          <div className="glass-card p-8 sm:p-12 border-[#BDD6B5]/15">
            <h3 className="text-2xl tracking-tight font-medium mb-4">
              {t('interest.communityHeading')}{' '}
              <span className="text-[#A4C3D2]">{t('interest.communityHeadingGreen')}</span>
            </h3>
            <p className="text-base text-white/78 mb-6 max-w-xl leading-relaxed">
              {t('interest.communityBody')}
            </p>

            {community.status === 'success' ? (
              <div className="py-4">
                <p className="text-[#BDD6B5] font-medium mb-1">You're on the list.</p>
                <p className="text-sm text-white/50">We'll reach out when the Community tier launches.</p>
              </div>
            ) : (
              <form onSubmit={community.submit} className="flex flex-col sm:flex-row gap-3 max-w-md">
                <Input
                  type="email"
                  required
                  placeholder={t('interest.emailPlaceholder')}
                  value={community.email}
                  onChange={(e) => community.setEmail(e.target.value)}
                  data-testid="waitlist-email-input"
                  className="bg-[#0E110E]/60 border-[#3D5638]/30 focus:border-[#BDD6B5] text-white placeholder:text-white/30 rounded-xl h-12 flex-1"
                />
                <button
                  type="submit"
                  disabled={community.status === 'submitting'}
                  data-testid="waitlist-submit-button"
                  className="bg-transparent border border-[#BDD6B5]/30 hover:border-[#BDD6B5]/60 disabled:opacity-50 text-[#BDD6B5] rounded-full px-6 py-3 font-medium transition-all duration-300 whitespace-nowrap"
                >
                  {community.status === 'submitting' ? 'Sending…' : t('interest.communitySubmit')}
                </button>
              </form>
            )}
            {community.status === 'error' && (
              <p className="text-red-400/80 text-xs mt-2">Something went wrong — {community.errorMsg || 'please try again'}.</p>
            )}
            <p className="text-xs text-white/30 mt-3">{t('interest.communityDisclaimer')}</p>
          </div>
        </motion.div>

      </div>
    </section>
  );
}
